import { REGEX_CONDITION } from '../../../constants';

type PlaceholderValues = {
  [key: string]: string;
};

/**
 * Replaces placeholders in a given string with corresponding placeholderValues.
 *
 * @param adjustLink - The string containing placeholders to be replaced.
 * @param placeholderValues - An object mapping placeholder keys to their replacement placeholderValues.
 * @returns A new string with placeholders replaced by their corresponding placeholderValues.
 *
 * The function iterates over the keys in the `values` object and replaces
 * each placeholder in the format `[key]` within the `adjustLink` string
 * with its associated value. If a key's value is `undefined`, it defaults
 * to an empty string. After replacing all placeholders, the function
 * performs a final replacement using the `REGEX_CONDITION.PLACEHOLDER`
 * to substitute it with '='.
 */
export function replaceAppAdjustLinkPlaceholder(adjustLink: string, placeholderValues: PlaceholderValues) {
  const keys = Object.keys(placeholderValues);
  const userPrefKey = '[UserPref]';
  const labelKey = '[label]';
  const hasUserPrefPlanceholder = adjustLink.includes(userPrefKey);
  const labelValue = placeholderValues.label;

  /**
   * Handles the replacement of the `[UserPref]` placeholder in the `adjustLink` string.
   *
   * If the `adjustLink` contains the `[UserPref]` placeholder and the `label` value exists
   * in the `placeholderValues` object, this block replaces `[UserPref]` with `[label]` in the `adjustLink`.
   * It then updates the `placeholderValues` object by assigning the `label` value to the `label` key
   * and removes the `UserPref` key from the `placeholderValues` object to prevent redundant replacements.
   *
   * Why do we need to do that? Glad that you asked!
   * - As we are mapping the placeholders of the `adjustLink` which are added in Contentful by the SEA Team, with the actual    `placeholderValues`;
   * - The `[UserPref]` palaceholder gets replaced with the value of the UserPref key from the `placeholderValues` Object
   * - Which is not the expected output.
   * - That's why we insert the correct value for the `[UserPref]` placeholder from the The `label` containing a custom structure of `pref` and `clickid`.
   *
   * @example
   * - label={cpc_gg_c58_pid_kwd-123_a183_clickid_gclid_CjwKBwE~}
   * - adgroup=a183
   * - etc.
   */
  if (hasUserPrefPlanceholder && labelValue) {
    adjustLink = adjustLink.replace(userPrefKey, labelKey);
    placeholderValues[labelKey] = labelValue;

    delete placeholderValues[userPrefKey];
  }

  const newLink = keys.reduce((result, key) => {
    const value = placeholderValues[key];

    return result.replace(`[${key}]`, value);
  }, adjustLink);

  return newLink.replace(REGEX_CONDITION.PLACEHOLDER, '=');
}
